import { useState } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { buildCopyWithSources, type CopyWithSources, sourceLine } from "@/lib/copyWithSources";
import type { PersistedCitationRef } from "@/types";

// Rendered through a detached React root rather than react-dom/server, which would add a
// second renderer to the bundle for one button.
function renderHtml(payload: CopyWithSources): string {
  const answer = document.createElement("div");
  const root = createRoot(answer);
  flushSync(() => root.render(<ReactMarkdown remarkPlugins={[remarkGfm]}>{payload.markdown}</ReactMarkdown>));
  const wrapper = document.createElement("div");
  wrapper.innerHTML = answer.innerHTML;
  root.unmount();

  const heading = document.createElement("p");
  const strong = document.createElement("strong");
  strong.textContent = "Sources";
  heading.append(strong);
  const list = document.createElement("p");
  payload.sources.forEach((source, i) => {
    if (i > 0) list.append(document.createElement("br"));
    list.append(sourceLine(source));
  });
  wrapper.append(heading, list);
  return wrapper.innerHTML;
}

async function writeToClipboard(text: string, html: string): Promise<void> {
  if (typeof ClipboardItem !== "undefined" && typeof navigator.clipboard?.write === "function") {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([text], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        }),
      ]);
      return;
    } catch {
      // Some browsers reject a text/html item; the plain-text copy below still carries everything.
    }
  }
  await navigator.clipboard.writeText(text);
}

type CopyState = "idle" | "copied" | "failed";

const LABELS: Record<CopyState, string> = {
  idle: "Copy with sources",
  copied: "Copied",
  failed: "Copy failed",
};

export function CopyWithSourcesButton({
  content,
  citations,
  disabled = false,
  className,
}: {
  content: string;
  citations: PersistedCitationRef[];
  disabled?: boolean;
  className?: string;
}) {
  const [state, setState] = useState<CopyState>("idle");

  async function onClick() {
    const payload = buildCopyWithSources(content, citations);
    if (!payload) return;
    try {
      await writeToClipboard(payload.text, renderHtml(payload));
      setState("copied");
    } catch {
      setState("failed");
    }
    setTimeout(() => setState("idle"), 1500);
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={state === "idle" ? "Copy answer with sources" : LABELS[state]}
      title={disabled ? "Sources are ready when the answer finishes" : "Copy the answer with numbered sources"}
      className={className}
    >
      {LABELS[state]}
    </button>
  );
}
